// Seeded randomness for reproducible deals. cyrb128 hashes a string seed into 128 bits that seed
// sfc32 (public domain; bryc's notes, .wiki/wiki/topics/tech-prng-and-daily-generation.md).
// A 128-bit state cannot reach every 52! deck order, so daily-deal fairness is guaranteed by
// pre-generating and validating deals server-side (M6), not by this PRNG.
export function cyrb128(str: string): [number, number, number, number] {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762
  for (let i = 0; i < str.length; i++) {
    const k = str.charCodeAt(i)
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067)
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233)
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213)
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179)
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067)
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233)
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213)
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179)
  h1 ^= h2 ^ h3 ^ h4; h2 ^= h1; h3 ^= h1; h4 ^= h1
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0]
}

export function sfc32(a: number, b: number, c: number, d: number): () => number {
  return () => {
    a |= 0; b |= 0; c |= 0; d |= 0
    const t = (((a + b) | 0) + d) | 0
    d = (d + 1) | 0
    a = b ^ (b >>> 9)
    b = (c + (c << 3)) | 0
    c = (c << 21) | (c >>> 11)
    c = (c + t) | 0
    return (t >>> 0) / 4294967296
  }
}

export function seeded(seed: string): () => number {
  const [a, b, c, d] = cyrb128(seed)
  const rand = sfc32(a, b, c, d)
  for (let i = 0; i < 15; i++) rand() // discard warm-up outputs
  return rand
}

export function shuffle<T>(items: readonly T[], rand: () => number): T[] {
  const out = items.slice()
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[out[i], out[j]] = [out[j]!, out[i]!]
  }
  return out
}

export function randomSeed(): string {
  try {
    const buf = new Uint32Array(2)
    crypto.getRandomValues(buf)
    return `${buf[0]!.toString(36)}${buf[1]!.toString(36)}`
  } catch {
    return Math.random().toString(36).slice(2)
  }
}
